/* eslint-disable @typescript-eslint/no-explicit-any */
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  featureControlService,
  type AddFeatureControl,
} from "@src/@api-services/feature-control";
import type { IFeatureToggle, IFeatureToggleSearchOptions } from "../types";

interface FeatureToggleState {
  data: IFeatureToggle[];
  total: number;
  params: IFeatureToggleSearchOptions;
  selectedFeature: IFeatureToggle | null;
  loading: boolean;
  detailLoading: boolean;
  actionLoading: boolean;
  error: string | null;
}

export const getFeatureToggles = createAsyncThunk(
  "featureToggles/getFeatureToggles",
  async (params: IFeatureToggleSearchOptions, { rejectWithValue }) => {
    try {
      const response = await featureControlService.getFeatureControls(params);
      const payload = response?.data?.data ?? {};
      const list = Array.isArray(payload.data)
        ? payload.data
        : Array.isArray(payload)
          ? payload
          : [];

      return {
        params,
        data: list as IFeatureToggle[],
        total: Number(payload.totalRecord ?? list.length ?? 0),
      };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch features"
      );
    }
  }
);

export const getFeatureToggleDetails = createAsyncThunk(
  "featureToggles/getFeatureToggleDetails",
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await featureControlService.getFeatureControlDetails(id);
      const payload = response?.data?.data ?? response?.data ?? null;
      return payload as IFeatureToggle;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch feature details"
      );
    }
  }
);

export const addFeatureToggle = createAsyncThunk(
  "featureToggles/addFeatureToggle",
  async (data: AddFeatureControl, { dispatch, getState, rejectWithValue }) => {
    try {
      const response = await featureControlService.addFeatureControl(data);
      const state = getState() as any;
      await dispatch(getFeatureToggles(state.featureToggles.params));
      return response.data.data as IFeatureToggle;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to add feature"
      );
    }
  }
);

export const updateFeatureToggle = createAsyncThunk(
  "featureToggles/updateFeatureToggle",
  async (
    {
      id,
      featureData,
    }: { id: string | number; featureData: Partial<AddFeatureControl> },
    { dispatch, getState, rejectWithValue }
  ) => {
    try {
      const response = await featureControlService.updateFeatureControl(
        id,
        featureData
      );
      const state = getState() as any;
      await dispatch(getFeatureToggles(state.featureToggles.params));
      return response.data.data as IFeatureToggle;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update feature"
      );
    }
  }
);

export const deleteFeatureToggle = createAsyncThunk(
  "featureToggles/deleteFeatureToggle",
  async (id: number, { dispatch, getState, rejectWithValue }) => {
    try {
      await featureControlService.deleteFeatureControl(id);
      const state = getState() as any;
      await dispatch(getFeatureToggles(state.featureToggles.params));
      return id;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete feature"
      );
    }
  }
);

const initialState: FeatureToggleState = {
  data: [],
  total: 0,
  params: {
    page: 1,
    pageSize: 10,
    orderBy: "createdAt",
    orderDirection: "DESC",
  },
  selectedFeature: null,
  loading: false,
  detailLoading: false,
  actionLoading: false,
  error: null,
};

export const featureToggleSlice = createSlice({
  name: "featureToggles",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSelectedFeature: (state) => {
      state.selectedFeature = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getFeatureToggles.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getFeatureToggles.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload.data;
        state.total = action.payload.total;
        state.params = action.payload.params;
      })
      .addCase(getFeatureToggles.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.data = [];
        state.total = 0;
      })
      .addCase(getFeatureToggleDetails.pending, (state) => {
        state.detailLoading = true;
        state.error = null;
      })
      .addCase(getFeatureToggleDetails.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.selectedFeature = action.payload;
      })
      .addCase(getFeatureToggleDetails.rejected, (state) => {
        state.detailLoading = false;
        state.selectedFeature = null;
      })
      .addCase(addFeatureToggle.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(addFeatureToggle.fulfilled, (state) => {
        state.actionLoading = false;
      })
      .addCase(addFeatureToggle.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload as string;
      })
      .addCase(updateFeatureToggle.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(updateFeatureToggle.fulfilled, (state) => {
        state.actionLoading = false;
      })
      .addCase(updateFeatureToggle.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload as string;
      })
      .addCase(deleteFeatureToggle.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(deleteFeatureToggle.fulfilled, (state) => {
        state.actionLoading = false;
      })
      .addCase(deleteFeatureToggle.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, clearSelectedFeature } = featureToggleSlice.actions;

export default featureToggleSlice.reducer;
